import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Badge,
  Chip,
  Divider,
  Skeleton,
  Avatar,
  Input,
  Textarea,
  NumberInput,
  Select,
  Checkbox,
  Radio,
  Switch,
  SegmentedControl,
  Field,
  SparkAmount,
} from "@/components/ui";
import { Search, Bell } from "@/icons";
import { SparkIcon, CurrencyIcon } from "@/brand";
import type { ButtonVariant } from "@/components/ui";
import { buildHead } from "@/lib/seo";

export const Route = createFileRoute("/kitchen-sink")({
  head: () => buildHead({ path: "/kitchen-sink", title: "Kitchen sink (dev)", noindex: true }),
  component: KitchenSink,
});

const VARIANTS: ButtonVariant[] = ["primary", "secondary", "outline", "ghost", "destructive", "success"];

function KitchenSink() {
  const [amount, setAmount] = useState("25");
  const [seg, setSeg] = useState<"buy" | "sell">("buy");
  const [chips, setChips] = useState<Set<string>>(new Set(["trending"]));
  const [checked, setChecked] = useState(true);
  const [radio, setRadio] = useState("market");
  const [sw, setSw] = useState(true);

  const toggleChip = (k: string) =>
    setChips((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });

  return (
    <div className="min-h-dvh bg-bg text-fg">
      <div className="mx-auto max-w-4xl px-5 py-10">
        <header className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">UI Kitchen Sink</h1>
            <p className="text-sm text-fg-muted">Phase 3 — core primitives · theme: game</p>
          </div>
        </header>

        <Section title="Buttons — variants">
          <div className="flex flex-wrap gap-3">
            {VARIANTS.map((v) => (
              <Button key={v} variant={v}>
                {v}
              </Button>
            ))}
          </div>
        </Section>

        <Section title="Buttons — sizes, states, icons">
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm">Small</Button>
            <Button size="md">Medium</Button>
            <Button size="lg">Large</Button>
            <Button size="icon" variant="outline" aria-label="Notifications">
              <Bell className="h-4 w-4" />
            </Button>
            <Button loading>Loading</Button>
            <Button disabled>Disabled</Button>
            <Button variant="primary">
              <SparkIcon className="h-4 w-4" /> Buy
            </Button>
          </div>
        </Section>

        <Section title="Selection controls">
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} /> Checkbox
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={false} disabled /> Disabled
            </label>
            <div className="flex items-center gap-4">
              {["market", "limit"].map((v) => (
                <label key={v} className="flex items-center gap-2 text-sm capitalize">
                  <Radio name="ks-order" checked={radio === v} onChange={() => setRadio(v)} /> {v}
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={sw} onCheckedChange={setSw} aria-label="Toggle" /> Switch
            </label>
          </div>
        </Section>

        <Section title="Chips & segmented">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-2">
              {["trending", "sports", "crypto", "politics", "new"].map((k) => (
                <Chip key={k} selected={chips.has(k)} onClick={() => toggleChip(k)} className="capitalize">
                  {k}
                </Chip>
              ))}
            </div>
            <SegmentedControl
              value={seg}
              onChange={setSeg}
              aria-label="Side"
              segments={[
                { value: "buy", label: "Buy" },
                { value: "sell", label: "Sell" },
              ]}
            />
          </div>
        </Section>

        <Section title="Inputs">
          <div className="grid max-w-xl gap-4">
            <Field label="Search" htmlFor="ks-search" hint="Leading icon + placeholder">
              <Input id="ks-search" placeholder="Search markets…" leftIcon={<Search className="h-4 w-4" />} />
            </Field>
            <Field label="Amount" htmlFor="ks-amount" hint="currency-aware stepper">
              <NumberInput id="ks-amount" value={amount} onChange={setAmount} step={5} min={0} suffix={<CurrencyIcon className="h-4 w-4" />} />
            </Field>
            <Field label="Order type" htmlFor="ks-type">
              <Select
                id="ks-type"
                defaultValue="market"
                options={[
                  { value: "market", label: "Market" },
                  { value: "limit", label: "Limit" },
                ]}
              />
            </Field>
            <Field label="Invalid example" htmlFor="ks-bad" error="This field is required">
              <Input id="ks-bad" invalid placeholder="Something's wrong" />
            </Field>
            <Field label="Notes" htmlFor="ks-notes">
              <Textarea id="ks-notes" placeholder="Write something…" />
            </Field>
          </div>
        </Section>

        <Section title="Display">
          <div className="flex flex-wrap items-center gap-3">
            <Badge tone="neutral">Neutral</Badge>
            <Badge tone="primary">Primary</Badge>
            <Badge tone="success">Open</Badge>
            <Badge tone="danger">Closed</Badge>
            <Badge tone="warning">Pending</Badge>
            <Divider vertical className="h-6" />
            <Avatar name="Ada Lovelace" />
            <Avatar name="Grace Hopper" size={32} />
            <Avatar src="/nope.png" name="Broken Img" />
            <Divider vertical className="h-6" />
            <SparkAmount value="1234560000" className="text-lg font-semibold" />
            <SparkAmount value="-45230" tone="auto" sign />
          </div>
        </Section>

        <Section title="Card & skeleton">
          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Will BTC close above 100k?</CardTitle>
                <CardDescription>Resolves Dec 31, 2026</CardDescription>
              </CardHeader>
              <CardBody>
                <div className="flex gap-2">
                  <Button variant="success" block>
                    Yes · <SparkAmount value="6200" />
                  </Button>
                  <Button variant="destructive" block>
                    No · <SparkAmount value="3800" />
                  </Button>
                </div>
              </CardBody>
            </Card>
            {/* A skeleton mirrors the layout of the card it stands in for. */}
            <Card>
              <CardHeader>
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-3.5 w-2/5" />
              </CardHeader>
              <CardBody>
                <div className="flex gap-2">
                  <Skeleton className="h-11 flex-1 rounded-xl" />
                  <Skeleton className="h-11 flex-1 rounded-xl" />
                </div>
              </CardBody>
            </Card>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-fg-muted">{title}</h2>
      {children}
    </section>
  );
}
